import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getJson } from "@/lib/http";
import { Upload, Download, FileText, AlertCircle, CheckCircle2, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Retailer, MediaFormat } from "@shared/schema";

interface CSVStore {
  name: string;
  address: string;
  phone?: string;
  managerName?: string;
  latitude?: number;
  longitude?: number;
  region: string;
  retailerId: number;
  formatId: number;
}

interface MediaFormatRequirement {
  mediaFormatId: number;
  quantity: number;
  notes?: string;
}

interface UploadResult {
  success: boolean;
  message: string;
  processedStores?: number;
  errors?: string[];
}

export default function BulkStoreUpload() {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [mediaRequirements, setMediaRequirements] = useState<MediaFormatRequirement[]>([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch retailers and formats for the CSV template
  const { data: retailers = [] } = useQuery<Retailer[]>({
    queryKey: ["/api/retailers"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/retailers');
      return getJson('/api/retailers');
    }
  });

  const { data: mediaFormats = [] } = useQuery<MediaFormat[]>({
    queryKey: ["/api/media-formats"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/media-formats');
      return getJson('/api/media-formats');
    }
  });

  const uploadMutation = useMutation({
    mutationFn: async (data: { file: File; retailerId: number; mediaRequirements: MediaFormatRequirement[] }) => {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('retailerId', data.retailerId.toString());
      formData.append('mediaRequirements', JSON.stringify(data.mediaRequirements));

      const response = await fetch("/api/stores/bulk-upload", {
        method: "POST",
        body: formData,
        credentials: 'include', // Include auth cookies
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        // Get the actual error message from the server
        const serverError = responseData?.error || responseData?.message || responseData?.detail;
        throw new Error(serverError || `Upload failed (${response.status})`);
      }

      return responseData;
    },
    onSuccess: (result) => {
      setUploadResult(result);
      if (result.success) {
        toast({
          title: "Upload Successful",
          description: `${result.processedStores} stores uploaded successfully`,
        });
        queryClient.invalidateQueries({ queryKey: ["/api/stores"] });
      }
    },
    onError: (error: any) => {
      // Also set the upload result so user can see errors on the page
      setUploadResult({
        success: false,
        message: error.message || "Upload failed",
        errors: [error.message]
      });
      toast({
        title: "Upload Failed",
        description: error.message || "There was an error uploading the CSV file",
        variant: "destructive",
      });
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const validTypes = ['text/csv', 'application/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const ext = file?.name.toLowerCase().slice(file.name.lastIndexOf('.')) || '';

    if (file && (validTypes.includes(file.type) || validExtensions.includes(ext))) {
      setCsvFile(file);
      setUploadResult(null);
    } else {
      toast({
        title: "Invalid File",
        description: "Please select a valid CSV file",
        variant: "destructive",
      });
    }
  };

  const handleUpload = () => {
    if (!csvFile) {
      toast({
        title: "No File Selected",
        description: "Please select a CSV file to upload",
        variant: "destructive",
      });
      return;
    }

    if (!selectedRetailerId) {
      toast({
        title: "No Retailer Selected",
        description: "Please select a retailer for these stores",
        variant: "destructive",
      });
      return;
    }

    uploadMutation.mutate({ file: csvFile, retailerId: selectedRetailerId, mediaRequirements });
  };

  const addMediaRequirement = () => {
    setMediaRequirements([...mediaRequirements, { mediaFormatId: 0, quantity: 1 }]);
  };

  const updateMediaRequirement = (index: number, field: keyof MediaFormatRequirement, value: any) => {
    const updated = [...mediaRequirements];
    updated[index] = { ...updated[index], [field]: value };
    setMediaRequirements(updated);
  };

  const removeMediaRequirement = (index: number) => {
    setMediaRequirements(mediaRequirements.filter((_, i) => i !== index));
  };

  const downloadTemplate = () => {
    const csvContent = [
      "name,address,phone,managerName,latitude,longitude,region",
      "Dunnes Stores Grafton Street,88 Grafton Street Dublin 2,+353-1-6770523,Sarah Johnson,53.3414,-6.2591,Dublin",
      "SuperValu Blackrock,19 Main Street Blackrock,+353-1-2888444,Emma Rodriguez,53.3014,-6.1781,Cork",
      "Dunnes Stores Stillorgan,Stillorgan Shopping Centre,+353-1-2888555,John Smith,53.2794,-6.2009,Dublin"
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'store_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Bulk Store Upload</h1>
        <p className="text-muted-foreground">
          Upload stores in bulk using CSV files and specify media format requirements.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              CSV Upload
            </CardTitle>
            <CardDescription>
              Upload a CSV file containing store information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="csv-file">Select CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileChange}
              />
            </div>

            {csvFile && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <FileText className="h-4 w-4" />
                <span className="text-sm">{csvFile.name}</span>
                <Badge variant="secondary">{(csvFile.size / 1024).toFixed(1)} KB</Badge>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="retailer-select">Select Retailer</Label>
              <Select
                value={selectedRetailerId?.toString() || ""}
                onValueChange={(value) => setSelectedRetailerId(parseInt(value))}
              >
                <SelectTrigger id="retailer-select">
                  <SelectValue placeholder="Select a retailer" />
                </SelectTrigger>
                <SelectContent>
                  {retailers.map((retailer: any) => (
                    <SelectItem key={retailer.id} value={retailer.id.toString()}>
                      {retailer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={downloadTemplate}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download Template
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!csvFile || !selectedRetailerId || uploadMutation.isPending}
                className="flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                {uploadMutation.isPending ? "Uploading..." : "Upload Stores"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Media Requirements Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Media Format Requirements
            </CardTitle>
            <CardDescription>
              Specify media formats and quantities for all uploaded stores
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {mediaRequirements.map((requirement, index) => (
              <div key={index} className="flex items-center gap-2 p-3 border rounded-lg">
                <div className="flex-1">
                  <Select
                    value={requirement.mediaFormatId.toString()}
                    onValueChange={(value) => updateMediaRequirement(index, 'mediaFormatId', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select media format" />
                    </SelectTrigger>
                    <SelectContent>
                      {mediaFormats.map((format: any) => (
                        <SelectItem key={format.id} value={format.id.toString()}>
                          {format.name} - {format.type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  type="number"
                  min="1"
                  value={requirement.quantity}
                  onChange={(e) => updateMediaRequirement(index, 'quantity', parseInt(e.target.value))}
                  className="w-20"
                  placeholder="Qty"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeMediaRequirement(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}

            <Button
              onClick={addMediaRequirement}
              variant="outline"
              className="w-full flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Media Requirement
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Results Section */}
      {uploadResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {uploadResult.success ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              Upload Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant={uploadResult.success ? "default" : "destructive"}>
              <AlertDescription>{uploadResult.message}</AlertDescription>
            </Alert>

            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">Errors:</h4>
                <ul className="list-disc list-inside space-y-1">
                  {uploadResult.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-600">{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>File Format Instructions</CardTitle>
          <p className="text-sm text-muted-foreground">Upload a CSV or Excel file (.xlsx) with store data</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Required columns */}
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-semibold text-green-700 mb-3">✓ Required Columns (must have these headers)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-green-50 p-3 rounded-md">
                  <code className="font-mono text-green-800 font-bold">name</code>
                  <p className="text-sm text-muted-foreground mt-1">Store name (e.g., "Tesco Express Dublin 1")</p>
                </div>
                <div className="bg-green-50 p-3 rounded-md">
                  <code className="font-mono text-green-800 font-bold">address</code>
                  <p className="text-sm text-muted-foreground mt-1">Full address (e.g., "123 Main Street, Dublin 1")</p>
                </div>
              </div>
            </div>

            {/* Optional columns */}
            <div className="border-l-4 border-gray-300 pl-4">
              <h4 className="font-semibold text-gray-600 mb-3">○ Optional Columns (add any of these headers)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <div className="p-2 bg-gray-50 rounded">
                  <code className="font-mono font-medium">phone</code>
                  <span className="text-muted-foreground ml-2">— Phone number</span>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <code className="font-mono font-medium">managerName</code>
                  <span className="text-muted-foreground ml-2">— Manager name</span>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <code className="font-mono font-medium">latitude</code>
                  <span className="text-muted-foreground ml-2">— GPS lat</span>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <code className="font-mono font-medium">longitude</code>
                  <span className="text-muted-foreground ml-2">— GPS long</span>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <code className="font-mono font-medium">region</code>
                  <span className="text-muted-foreground ml-2">— Region/area</span>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <code className="font-mono font-medium">notes</code>
                  <span className="text-muted-foreground ml-2">— Store notes</span>
                </div>
              </div>
            </div>

            {/* Example */}
            <div className="bg-slate-100 p-4 rounded-lg">
              <h4 className="font-medium mb-2 text-sm">Example CSV content:</h4>
              <pre className="text-xs font-mono bg-white p-3 rounded border overflow-x-auto">
                {`name,address,phone,managerName
Tesco Express Dublin 1,123 Main Street Dublin 1,01-1234567,John Smith
Dunnes Stores Cork,456 High Street Cork,021-7654321,Jane Doe`}
              </pre>
            </div>

            <p className="text-sm bg-blue-50 text-blue-700 p-3 rounded-md">
              <strong>💡 Tip:</strong> The retailer is selected from the dropdown above — no need to include it in your file.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}